import React, { useState, useEffect, useRef } from "react";
import SearchIcon from "../Icons/SearchIcon";
import { useDispatch } from "react-redux";
import { updateFiltersStates } from "../../store/actions/UserActions";

let debounceTimeout;

const DEFAULT_OPTIONS = [];

const SearchSiteSelect = ({
  label = null,
  options = DEFAULT_OPTIONS,
  placeholder = "Search...",
  onSelect,
  className = "",
  style = null,
  errors,
  value = "",
  isLoading = false,
  onSearch = () => { },
  showClearButton = true,
  required = false,
  disabled = false,
  disableSearch = false,
}) => {
  const [search, setSearch] = useState("");
  const [filteredOptions, setFilteredOptions] = useState(options);
  const [showDropdown, setShowDropdown] = useState(false);
  const [isTyping, setIsTyping] = useState(false);
  const dropdownRef = useRef();
  const dispatch = useDispatch();

  useEffect(() => {
    setFilteredOptions(options);
  }, [options]);

  useEffect(() => {
    if (!isTyping) {
      if (value) {
        const selected = options.find(
          (opt) => opt?.value === value || opt?.label === value
        );

        if (selected && selected.label) {
          setSearch(selected.label);
          setFilteredOptions(
            options.filter(
              (opt) =>
                opt?.label &&
                opt.label.toLowerCase().includes(selected.label.toLowerCase())
            )
          );
        } else {
          setSearch(value || "");
          setFilteredOptions(options);
        }
      } else {
        setSearch("");
        setFilteredOptions(options);
      }
    }
  }, [value, options, isTyping]);

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target)) {
        setShowDropdown(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const handleSelect = (option) => {
    setSearch(option.label);
    setShowDropdown(false);
    setIsTyping(false);
    onSelect(option);
  };

  const handleSearch = (data) => {
    setIsTyping(true);
    setSearch(data);

    if (data?.trim() === "") {
      clearTimeout(debounceTimeout);
      setFilteredOptions(options);
      onSearch("");
      return;
    }

    clearTimeout(debounceTimeout);
    debounceTimeout = setTimeout(() => {
      onSearch(data);
    }, 400);

    setFilteredOptions(
      options.filter((opt) =>
        opt.label.toLowerCase().includes(data.toLowerCase())
      )
    );
  };

  const handleClear = () => {
    setSearch("");
    setFilteredOptions(options);
    setShowDropdown(false);
    onSelect({ value: "", label: "" });
    onSearch("");
  };

  return (
    <div ref={dropdownRef}>
      {label && (
        <label className="form-label">
          {label} {required && <span className="text-danger">*</span>}
        </label>
      )}

      <div className="position-relative w-100">
        {!disableSearch && (
          <span className="search-icon position-absolute top-50 translate-middle-y ms-2">
            <SearchIcon />
          </span>
        )}

        <input
          type="text"
          style={disableSearch ? { ...style, paddingLeft: "15px", cursor: "pointer" } : style}
          className={`form-control search-control  pe-5 ${errors ? "is-invalid" : ""} ${className}`}
          placeholder={placeholder}
          value={search}
          onChange={(e) => !disableSearch && handleSearch(e.target.value)}
          onFocus={() => { if (!disabled) setShowDropdown(true); }}
          readOnly={disableSearch}
          disabled={disabled}
        />

        {/* ❌ Clear Button */}
        {(search && showClearButton && !disabled) && (
          <button
            type="button"
            onClick={handleClear}
            className="position-absolute top-50 end-0 translate-middle-y me-2 border-0 bg-transparent"
            style={{
              cursor: "pointer",
              fontSize: "18px",
              lineHeight: "1",
              color: "#888",
            }}
            title="Clear"
          >
            ×
          </button>
        )}

        {showDropdown && (
          <ul
            className="list-group position-absolute w-100 mt-1 border rounded bg-white shadow-sm"
            style={{ maxHeight: "200px", overflowY: "auto", zIndex: 1050 }}
          >
            {isLoading && <li className="list-group-item">Loading...</li>}

            {!isLoading && filteredOptions.length > 0
              ? filteredOptions.map((opt, idx) => (
                <li
                  key={idx}
                  onClick={() => handleSelect(opt)}
                  className={`list-group-item list-group-item-action ${opt.value === value ? "active" : ""
                    }`}
                  style={{ cursor: "pointer", fontSize: "13.5px" }}
                >
                  {opt.label}
                  {opt.companyName ? `, ${opt.companyName}` : ""}
                </li>
              ))
              : !isLoading && (
                <li className="list-group-item text-secondary">
                  No options found
                </li>
              )}
          </ul>
        )}
      </div>

      {errors && <div className="invalid-feedback d-block">{errors}</div>}
    </div>
  );
};

export default SearchSiteSelect;
